import React, { useState } from "react";
import { Button, Typography, Box } from "@mui/material";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";

interface ImageUploaderProps {
  folderPath: string;  // Main folder, e.g., "properties"
  uniqueId: string;    // Dynamic key or ID for the subfolder
  onUploadSuccess: (urls: string[]) => void;  // Callback to return the image URLs after upload
}

const ImageUploader: React.FC<ImageUploaderProps> = ({ folderPath, uniqueId, onUploadSuccess }) => {
  const [files, setFiles] = useState<File[]>([]);  // Store multiple files
  const [progress, setProgress] = useState<number[]>([]);  // Store progress for each file
  const [previews, setPreviews] = useState<string[]>([]);  // Store image previews

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []); // Convert FileList to Array
    setFiles(selectedFiles);

    // Generate previews for the selected files
    const filePreviews = selectedFiles.map((file) => URL.createObjectURL(file));
    setPreviews(filePreviews);
  };

  const handleUpload = () => {
    if (files.length > 0) {
      const storage = getStorage();
      const uploadPromises = files.map((file, index) => {
        const fileRef = ref(storage, `${folderPath}/${uniqueId}/${file.name}`);
        const uploadTask = uploadBytesResumable(fileRef, file);

        return new Promise<string>((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              // Update progress for the current file
              const currentProgress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setProgress((prevProgress) => {
                const newProgress = [...prevProgress];
                newProgress[index] = currentProgress;
                return newProgress;
              });
            },
            (error) => {
              console.error("Upload error:", error);
              reject(error);
            },
            () => {
              // Get the download URL after upload is complete
              getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                resolve(downloadURL);
              });
            }
          );
        });
      });

      // Wait for all uploads to complete
      Promise.all(uploadPromises)
        .then((downloadURLs) => {
          onUploadSuccess(downloadURLs);  // Return the array of image URLs after all uploads succeed
        })
        .catch((error) => {
          console.error("Error uploading files:", error);
        });
    }
  };

  return (
    <Box>
      {/* Input element with multiple attribute */}
      <input type="file" multiple onChange={handleFileChange} accept="image/*" />
      
      {/* Image Previews */}
      {previews.length > 0 && (
        <Box mt={2}>
          <Typography variant="subtitle1">Image Previews:</Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {previews.map((src, index) => (
              <Box key={index}>
                <img
                  src={src}
                  alt={`Preview ${index}`}
                  width="100"
                  height="100"
                  style={{ objectFit: 'cover', borderRadius: '4px' }}
                />
                <Typography variant="body2">{files[index]?.name}</Typography>
                <Typography variant="body2">
                  {progress[index] ? `Upload Progress: ${progress[index].toFixed(2)}%` : "Not uploaded"}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {files.length > 0 && (
        <Button
          variant="contained"
          color="primary"
          onClick={handleUpload}
          sx={{ mt: 2 }}
        >
          Upload {files.length} Images
        </Button>
      )}
    </Box>
  );
};

export default ImageUploader;
